import { t } from "@/i18n-js/instance";
import { formatPaywallCurrencyAmount } from "@/react/helpers/paywalls/currencies";
import {
  isInstallmentsPrice,
  isOneTimePrice,
} from "@circle-react/helpers/paywallPriceHelpers";
import type { CouponApplication } from "@circle-react/types";
import { defaultFormatPaywallCurrency } from "../constants";
import type { PriceScenariosProps } from "../types";
import { Title } from "./Title";

const getTitleAndOldTitle = (
  totalPrice: number,
  priceDiscounted: number,
  paywall: PriceScenariosProps["paywall"],
  couponApplication?: CouponApplication,
) => {
  let oldTitle;
  let title;

  if (couponApplication) {
    oldTitle = formatPaywallCurrencyAmount(
      paywall.currency,
      totalPrice,
      defaultFormatPaywallCurrency,
    );
    title = formatPaywallCurrencyAmount(
      paywall.currency,
      priceDiscounted,
      defaultFormatPaywallCurrency,
    );
  } else {
    title = formatPaywallCurrencyAmount(
      paywall.currency,
      totalPrice,
      defaultFormatPaywallCurrency,
    );
  }

  return {
    title,
    oldTitle,
  };
};

const getSubscriptionPrice = ({
  price,
  paywall,
  couponApplication,
}: PriceScenariosProps) => {
  const totalPrice = price.amount;
  const priceDiscounted =
    price.amount - (couponApplication?.discount_amount || 0);

  return {
    ...getTitleAndOldTitle(
      totalPrice,
      priceDiscounted,
      paywall,
      couponApplication,
    ),
    suffix: `/${t(["paywall_checkout.price_value.interval_suffix", price.interval_type])}`,
  };
};

const getInstallmentPrice = ({
  price,
  paywall,
  couponApplication,
}: PriceScenariosProps) => {
  if (!isInstallmentsPrice(price)) {
    return null;
  }

  const totalPrice = Math.floor(
    (price.amount - (price.upfront_payment_amount || 0)) /
      (price.installments_count || 1),
  );
  const priceDiscounted =
    totalPrice - (couponApplication?.discount_amount || 0);

  return {
    prefix: `${price.installments_count}x`,
    ...getTitleAndOldTitle(
      totalPrice,
      priceDiscounted,
      paywall,
      couponApplication,
    ),
    suffix: `/${t(["paywall_checkout.price_value.interval_suffix", price.interval_type])}`,
  };
};

const getOneTimePrice = ({
  price,
  paywall,
  couponApplication,
}: PriceScenariosProps) => {
  if (!isOneTimePrice(price)) {
    return null;
  }

  const totalPrice = price.amount;
  const priceDiscounted =
    price.amount - (couponApplication?.discount_amount || 0);

  return {
    ...getTitleAndOldTitle(
      totalPrice,
      priceDiscounted,
      paywall,
      couponApplication,
    ),
  };
};

export const Value = (props: PriceScenariosProps) => (
  <Title
    {...(getOneTimePrice(props) ||
      getInstallmentPrice(props) ||
      getSubscriptionPrice(props))}
  />
);
